import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Drawer,
  Space,
  Button,
  Form,
  Input,
  Divider,
  Badge
} from 'antd';
import Text from 'antd/lib/typography/Text';
import { TemplatesPOSTRequest } from '../../../../interfaces/backend/TemplatesPOSTRequest';
import styled from 'styled-components';
import TemplateCardContextMenu from '../TemplateCardContextMenu';
import { liquidizeTemplate } from '../../LiquidizedPreview';
import { InAppPreview } from './InAppPreview';
import { blue } from '@ant-design/colors';
import { Template } from '../../../../interfaces/backend/Template';
import { Notification } from '../../../../interfaces/backend/Notification';
import { TemplatesPATCHRequest } from '../../../../interfaces/backend/TemplatesPATCHRequest';

const FormItem = Form.Item;
type InAppTemplate = Extract<Template, { channel: 'INAPP_WEB' }>;

interface Props {
  template: Extract<Template, { channel: 'INAPP_WEB' }>;
  deleteTemplate: (channel: string, templateId: string) => Promise<void>;
  makeDefault: (channel: string, templateId: string) => Promise<void>;
  patchTemplate: (
    channel: string,
    templateId: string,
    params: Record<string, unknown>
  ) => Promise<void>;
  createTemplate: (
    channel: string,
    params: TemplatesPOSTRequest
  ) => Promise<void>;
  setTemplateBeingDuplicated: (template: Template) => void;
  notification: Notification;
}

const CustomCard = styled(Card)`
  cursor: pointer;
  &:hover {
    border: 1px solid ${blue.primary};
    transition: border 0.3s ease;
  }
`;

const InAppTemplateCard: React.FC<Props> = (props: Props) => {
  const batching =
    props.notification.options?.INAPP_WEB?.instant?.batching ?? false;

  const [openedTemplateEditor, setOpenedTemplateEditor] =
    useState<InAppTemplate>();
  const [form] = Form.useForm();
  const [saveTemplate, changeSaveTemplate] = useState(false);
  const [previewError, setPreviewError] = useState<React.ReactNode>();
  const [previewWarning, setPreviewWarning] = useState<React.ReactNode>();
  const [liquidizedTemplate, setLiquidizedTemplate] = useState<InAppTemplate>();
  const [liquidizedBatchTemplate, setLiquidizedBatchTemplate] =
    useState<InAppTemplate>();

  const generateActionButtons = (): JSX.Element[] => {
    const buttons = [
      <TemplateCardContextMenu
        template={props.template}
        setTemplateBeingDuplicated={props.setTemplateBeingDuplicated}
        makeDefault={props.makeDefault}
        deleteTemplate={props.deleteTemplate}
      />
    ];

    return buttons;
  };

  const onFinish = async (value: {
    title: string;
    imageURL: string;
    redirectURL: string;
    batch_title: string;
    batch_imageURL: string;
    batch_redirectURL: string;
  }) => {
    changeSaveTemplate(true);
    const body: TemplatesPATCHRequest = {
      instant: {
        title: value.title,
        imageURL: value.imageURL,
        redirectURL: value.redirectURL
      }
    };
    if (batching) {
      body.batch = {
        title: value.batch_title,
        imageURL: value.batch_imageURL,
        redirectURL: value.batch_redirectURL
      };
    }
    await props.patchTemplate(
      props.template.channel,
      props.template.templateId,
      body
    );
    changeSaveTemplate(false);
    setOpenedTemplateEditor(undefined);
  };

  useEffect(() => {
    const { result, warning, error } = liquidizeTemplate(props.template);
    const batched = liquidizeTemplate({
      ...props.template,
      title: props.template.batch.title ?? '',
      imageURL: props.template.batch.imageURL ?? '',
      redirectURL: props.template.batch.redirectURL ?? ''
    });

    setPreviewError(error || batched.error);
    setPreviewWarning(warning || batched.warning);

    if (result) {
      const liquidizedTemplate = result as InAppTemplate;
      setLiquidizedTemplate(liquidizedTemplate);
    }
    if (result) {
      const batchLiquidizedTemplate = batched.result as InAppTemplate;
      setLiquidizedBatchTemplate(batchLiquidizedTemplate);
    }
  }, [props.template]);

  return (
    <Col span={24} lg={12} xl={8}>
      <CustomCard
        onClick={() => {
          setOpenedTemplateEditor(liquidizedTemplate);
        }}
        title={<Row>In-App</Row>}
        actions={generateActionButtons()}
      >
        {!previewError && (
          <div
            style={{
              padding: batching ? '15px 0px 0px 0px' : '0'
            }}
          >
            {batching ? (
              <Badge.Ribbon
                text="Individual"
                style={{
                  marginTop: -25,
                  marginRight: -1
                }}
                color="blue"
              >
                <InAppPreview
                  title={liquidizedTemplate?.title}
                  imageURL={liquidizedTemplate?.imageURL}
                  redirectURL={liquidizedTemplate?.redirectURL}
                  time={new Date().toISOString()}
                  uniqueId={props.template.templateId}
                />
              </Badge.Ribbon>
            ) : (
              <InAppPreview
                title={liquidizedTemplate?.title}
                imageURL={liquidizedTemplate?.imageURL}
                redirectURL={liquidizedTemplate?.redirectURL}
                time={new Date().toISOString()}
                uniqueId={props.template.templateId}
              />
            )}

            {batching && (
              <div style={{ marginTop: 18 }}>
                <Badge.Ribbon
                  text="Batched"
                  style={{
                    marginTop: -25,
                    marginRight: -1
                  }}
                  color="purple"
                >
                  <InAppPreview
                    title={liquidizedBatchTemplate?.title}
                    imageURL={liquidizedBatchTemplate?.imageURL}
                    redirectURL={liquidizedBatchTemplate?.redirectURL}
                    time={new Date().toISOString()}
                    uniqueId={props.template.templateId + '-batch'}
                  />
                </Badge.Ribbon>
              </div>
            )}
          </div>
        )}
      </CustomCard>
      <Drawer
        destroyOnClose
        open={openedTemplateEditor ? true : false}
        onClose={() => setOpenedTemplateEditor(undefined)}
        title="In-App Template Editor"
        width={500}
        styles={{
          body: {
            paddingBottom: 80
          }
        }}
        extra={
          <Space>
            <Button
              loading={saveTemplate}
              onClick={() => {
                form.submit();
              }}
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
            <Button
              onClick={(): void => {
                setOpenedTemplateEditor(undefined);
              }}
            >
              Cancel
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          onFinish={(value) => {
            onFinish(value);
          }}
          layout="vertical"
        >
          <FormItem
            label="Title:"
            name="title"
            initialValue={props.template.title}
          >
            <Input.TextArea placeholder={props.template.title} autoSize />
          </FormItem>
          <FormItem
            label="Image URL:"
            name="imageURL"
            initialValue={props.template.imageURL}
          >
            <Input.TextArea placeholder={props.template.imageURL} autoSize />
          </FormItem>
          <FormItem
            label="On Click (Redirect URL):"
            name="redirectURL"
            initialValue={props.template.redirectURL}
          >
            <Input.TextArea placeholder={props.template.redirectURL} autoSize />
          </FormItem>

          {batching && (
            <>
              <Divider
                children={
                  <>
                    <Text>Batched:</Text>
                  </>
                }
                orientation="left"
                orientationMargin={0}
              />
              <FormItem
                label="Title:"
                name="batch_title"
                initialValue={props.template.batch.title}
              >
                <Input.TextArea
                  placeholder={props.template.batch.title}
                  autoSize
                />
              </FormItem>
              <FormItem
                label="Image URL:"
                name="batch_imageURL"
                initialValue={props.template.batch.imageURL}
              >
                <Input.TextArea
                  placeholder={props.template.batch.imageURL}
                  autoSize
                />
              </FormItem>
              <FormItem
                label="On Click (Redirect URL):"
                name="batch_redirectURL"
                initialValue={props.template.batch.redirectURL}
              >
                <Input.TextArea
                  placeholder={props.template.batch.redirectURL}
                  autoSize
                />
              </FormItem>
            </>
          )}
        </Form>
      </Drawer>
      {previewError}
      {previewWarning}
    </Col>
  );
};

export default InAppTemplateCard;
